import { call, debounce, fork, put, select, takeEvery, all } from 'redux-saga/effects';
import { v4 as uuidv4 } from 'uuid';
import { stopSubmit, touch } from 'redux-form';
import {
	getCompaniesSearchValueSelector,
	getDataForUpdateCompanyAdminSettingsSelector,
	getDataForUpdateCompanySelector,
	getDataForUpdateCompanySettingsSelector,
	getDataForUpdateCompanyAboutInfoSelector,
	getAboutFormIsInvalidSelector,
	getFormSettingIsInvalidSelector,
	getIsProfileCompanyFormSelector,
	getFormIsInvalidSelector,
} from './selectors';
import {
	transformToCompanyValidationErrors,
	transformToValidationErrors,
	transformToValidationSettingErrors,
} from './utils';
import {
	GET_COMPANIES,
	GET_COMPANIES_TYPES,
	GET_COMPANY_ADMIN_SETTINGS,
	GET_COMPANY_INFO,
	GET_COMPANY_SETTINGS,
	GET_ORG_TYPES,
	GET_TAX_RATES,
	GET_TAX_SYSTEMS,
	GET_COMPANY_ABOUT_INFO,
	setCompaniesAction,
	setCompaniesTypesAction,
	setCompanyAdminSettingsAction,
	setCompanyInfoAction,
	setCompanySettingsAction,
	setIsCompaniesLoadingAction,
	setIsCompaniesTypesLoading,
	setIsCompanyAdminSettingsLoadingAction,
	setIsCompanyAdminSettingsSavingAction,
	setIsCompanyInfoLoadingAction,
	setIsCompanySavingAction,
	setIsCompanySettingsLoadingAction,
	setIsCompanySettingsSavingAction,
	setIsOrgTypesLoading,
	setIsTaxRatesLoading,
	setIsTaxSystemsLoading,
	setIsDeliveryTypesLoading,
	setIsTonnagesTypesLoading,
	setIsCarTypesLoading,
	setIsModesTypesLoading,
	setIsCompanyAboutLoadingAction,
	setOrgTypesAction,
	setTaxRatesAction,
	setTaxSystemsAction,
	setCompaniesAboutAction,
	UPDATE_COMPANY,
	UPDATE_COMPANY_ADMIN_SETTINGS,
	UPDATE_COMPANY_SETTINGS,
	CREATE_COMPANY_ABOUT_INFO,
	GET_OPTIONS_TYPES,
	getCompanyDeliveryAction,
	getCompanyCarAction,
	getCompanyTonnagesAction,
	getCompanyModesAction,
	setIsCompaniesPatchLoadingAction,
	setIsStagesLoadingAction,
	setStagesAction,
	setIsDocumentsTypesLoadingAction,
	setDocumentsTypesAction,
	GET_STAGES,
	GET_DOCUMENTS_TYPES,
} from './actions';
import {
	getCompaniesBySearchRequest,
	getCompaniesTypesDictionaryRequest,
	getCompanyAdminSettingsRequest,
	getCompanyRequest,
	getCompanySettingsRequest,
	getOrgTypesRequest,
	getTaxRatesRequest,
	getTaxSystemRequest,
	updateCompanyAdminSettingsRequest,
	updateCompanyRequest,
	updateCompanySettingsRequest,
	getCompaniesAboutInfoRequest,
	createCompanyDescriptionRequest,
	getDeliveryMethodsRequest,
	getCarTypesRequest,
	getTonnagesDictionaryRequest,
	getModesDictionaryRequest,
	getDocumentTypesRequest,
	getStagesMyTransportsRequest,
} from '../../../api/requests';
import { notificationInit } from '../../../modules/notifications/actions';
import { DISMISS_TIME } from '../../../api/constants';
import {
	COMPANIES_FORM_SERVER_RESPONSE_ERROR,
	FIELDS_FOR_ADMIN_SETTINGS_VALIDATE,
	FIELDS_FOR_SETTINGS_VALIDATE,
	FIELDS_FOR_VALIDATE,
	FORM_ABOUT_NAME,
	FORM_NAME,
	FORM_SETTINGS_ADMIN_NAME,
	FORM_SETTINGS_NAME,
	REQUEST_ERROR_MESSAGE,
} from './constants';
import { getDictionaryDataSelector } from '../companies/selectors';
import { getDictionaryDataAction, setDictionaryDataAction } from '../companies/actions';
import { errorHandler } from '../../../api/utils';
import { removeModalQuery } from '../../../utils/removeModalQuery';

export function* getCompanyInfoSaga({ id }) {
	try {
		yield put(setIsCompanyInfoLoadingAction(true));

		const { data, message, toast } = yield call(getCompanyRequest, id);

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			yield put(setCompanyInfoAction(data));
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsCompanyInfoLoadingAction(false));
	}
}

export function* updateCompanySaga({ payload: { id, redirect, searchQuery } }) {
	try {
		yield put(setIsCompanySavingAction(true));

		const isInvalid = yield select(getFormIsInvalidSelector());

		if (isInvalid) return;

		const dataForSave = yield select(getDataForUpdateCompanySelector());

		const isProfile = yield select(getIsProfileCompanyFormSelector());

		const { data, errors, message, toast } = yield call(updateCompanyRequest, id, dataForSave);

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			const companies = yield select(getDictionaryDataSelector());

			const updatedCompany = {
				id: data.id,
				name: data.name || '-',
				inn: data.inn || '-',
				ogrn: data.ogrn || '-',
				contact: {
					phone: data.contact?.phone || '-',
					last_name: data.contact?.last_name || '-',
					first_name: data.contact?.first_name || '-',
				},
				phone: data.contact.phone
					? `${data.contact.phone}${data.contact.extension ? `, ${data.contact.extension}` : ''}`
					: '-',
				link: `?${searchQuery ? `${searchQuery}&` : ''}modal=company&id=${data.id}`,
			};

			yield put(
				setDictionaryDataAction(
					companies.map(company => {
						return company.id === data.id ? updatedCompany : company;
					}),
				),
			);

			yield put(setCompanyInfoAction(data));
			redirect();

			if (!isProfile) {
				yield put(getDictionaryDataAction({ searchQuery: removeModalQuery(searchQuery), noLoading: true }));
			}
		} else {
			if (errors) {
				yield put(touch(FORM_NAME, ...FIELDS_FOR_VALIDATE));
				yield put(stopSubmit(FORM_NAME, transformToValidationErrors(errors)));
			}

			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsCompanySavingAction(false));
	}
}

export function* getCompaniesTypesSaga() {
	try {
		yield put(setIsCompaniesTypesLoading(true));

		const { data, message, toast } = yield call(getCompaniesTypesDictionaryRequest, '?receive=all');

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			const mappedData = data.map(item => ({ value: item.id, key: item.id, text: item.name }));

			yield put(setCompaniesTypesAction([{ value: '', text: 'Нет', key: 'null' }, ...mappedData]));
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsCompaniesTypesLoading(false));
	}
}

export function* getCompaniesSaga() {
	try {
		yield put(setIsCompaniesLoadingAction(true));

		const searchQuery = yield select(getCompaniesSearchValueSelector());

		const response = yield call(getCompaniesBySearchRequest, searchQuery);

		if (response?.suggestions) {
			yield put(setCompaniesAction(response.suggestions));
		} else {
			throw new Error(response?.message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsCompaniesLoadingAction(false));
	}
}

export function* createCompaniesInfoSaga({ payload: { id, redirect } }) {
	try {
		yield put(setIsCompaniesPatchLoadingAction(true));

		const isInvalid = yield select(getAboutFormIsInvalidSelector());

		if (isInvalid) return;

		const dataForSave = yield select(getDataForUpdateCompanyAboutInfoSelector());

		const { data, errors, message, toast } = yield call(createCompanyDescriptionRequest, id, dataForSave);

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			redirect();
		} else {
			if (errors) {
				const errorKeys = Object.keys(errors).map(key =>
					key.replace(/^transports\.(\d+)\./, 'transports[$1].'),
				);

				yield all(errorKeys.map(field => put(touch(FORM_ABOUT_NAME, field))));
				yield put(stopSubmit(FORM_ABOUT_NAME, transformToCompanyValidationErrors(errors)));
			}

			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsCompaniesPatchLoadingAction(false));
	}
}

export function* getDeliveryMethodsSaga() {
	try {
		yield put(setIsDeliveryTypesLoading(true));
		const { data, errors, message, toast } = yield call(getDeliveryMethodsRequest, '?receive=all');

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			yield put(getCompanyDeliveryAction(data.map(item => ({ key: item.id, value: item.id, text: item.name }))));
		} else {
			if (errors) {
				yield put(touch(FORM_ABOUT_NAME, ...COMPANIES_FORM_SERVER_RESPONSE_ERROR));
				yield put(stopSubmit(FORM_ABOUT_NAME, transformToCompanyValidationErrors(errors)));
			}

			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsDeliveryTypesLoading(false));
	}
}

export function* getCarTypesSaga() {
	try {
		yield put(setIsCarTypesLoading(true));

		const { data, errors, message, toast } = yield call(getCarTypesRequest, '?receive=all');

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			yield put(getCompanyCarAction(data.map(item => ({ key: item.id, value: item.id, text: item.name }))));
		} else {
			if (errors) {
				yield put(touch(FORM_ABOUT_NAME, ...COMPANIES_FORM_SERVER_RESPONSE_ERROR));
				yield put(stopSubmit(FORM_ABOUT_NAME, transformToCompanyValidationErrors(errors)));
			}

			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsCarTypesLoading(false));
	}
}

export function* getTonnagesTypesSaga() {
	try {
		yield put(setIsTonnagesTypesLoading(true));
		const { data, errors, message, toast } = yield call(getTonnagesDictionaryRequest, '?receive=all');

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			yield put(
				getCompanyTonnagesAction([
					{ text: 'Нет', value: 'none', key: 'none' },
					...data.map(item => ({ key: item.id, value: item.id, text: item.name })),
				]),
			);
		} else {
			if (errors) {
				yield put(touch(FORM_ABOUT_NAME, ...COMPANIES_FORM_SERVER_RESPONSE_ERROR));
				yield put(stopSubmit(FORM_ABOUT_NAME, transformToCompanyValidationErrors(errors)));
			}

			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsTonnagesTypesLoading(false));
	}
}

export function* getModesTypesSaga() {
	try {
		yield put(setIsModesTypesLoading(true));
		const { data, errors, message, toast } = yield call(getModesDictionaryRequest, '?receive=all');

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			yield put(getCompanyModesAction(data.map(item => ({ key: item.id, value: item.id, text: item.name }))));
		} else {
			if (errors) {
				yield put(touch(FORM_ABOUT_NAME, ...COMPANIES_FORM_SERVER_RESPONSE_ERROR));
				yield put(stopSubmit(FORM_ABOUT_NAME, transformToCompanyValidationErrors(errors)));
			}

			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsModesTypesLoading(false));
	}
}

export function* getCompaniesAboutSaga({ payload }) {
	try {
		yield put(setIsCompanyAboutLoadingAction(true));

		const { data, errors, message, toast } = yield call(getCompaniesAboutInfoRequest, payload);

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			yield put(setCompaniesAboutAction(data));
		} else {
			if (errors) {
				yield put(touch(FORM_ABOUT_NAME, ...COMPANIES_FORM_SERVER_RESPONSE_ERROR));
				yield put(stopSubmit(FORM_ABOUT_NAME, transformToCompanyValidationErrors(errors)));
			}

			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsCompanyAboutLoadingAction(false));
	}
}

export function* getOrgTypesSaga() {
	try {
		yield put(setIsOrgTypesLoading(true));

		const { data, message, toast } = yield call(getOrgTypesRequest, '?receive=all');

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			const mappedData = data.map(item => ({ value: item.id, key: item.id, text: item.name }));

			yield put(setOrgTypesAction([{ value: '', text: 'Нет', key: 'null' }, ...mappedData]));
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsOrgTypesLoading(false));
	}
}
export function* getTaxSystemsSaga() {
	try {
		yield put(setIsTaxSystemsLoading(true));

		const { data, message, toast } = yield call(getTaxSystemRequest, '?receive=all');

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			const mappedData = data.map(item => ({ value: item.id, key: item.id, text: item.name }));

			yield put(setTaxSystemsAction([{ value: '', text: 'Нет', key: 'null' }, ...mappedData]));
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsTaxSystemsLoading(false));
	}
}
export function* getTaxRatesSaga({ payload }) {
	try {
		yield put(setIsTaxRatesLoading(true));

		const taxSystemId = payload?.taxSystemId;

		const path = taxSystemId ? { taxSystemId } : null;

		const { data, message, toast } = yield call(getTaxRatesRequest, path, '?receive=all');

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			const mappedData = data.map(item => ({
				value: item.id,
				key: item.id,
				text: item.name_with_symbol,
				disabled: !item.is_available,
			}));

			yield put(setTaxRatesAction([{ value: '', text: 'Нет', key: 'null' }, ...mappedData]));
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsTaxRatesLoading(false));
	}
}
export function* getCompanySettingsSaga({ payload }) {
	try {
		yield put(setIsCompanySettingsLoadingAction(true));

		const { data, message, toast } = yield call(getCompanySettingsRequest, payload);

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			yield put(setCompanySettingsAction(data));
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsCompanySettingsLoadingAction(false));
	}
}

export function* updateCompanySettingsSaga({ payload: { id, redirect } }) {
	try {
		yield put(setIsCompanySettingsSavingAction(true));

		const isInvalid = yield select(getFormSettingIsInvalidSelector());

		if (isInvalid) return;

		const dataForSave = yield select(getDataForUpdateCompanySettingsSelector());

		const { data, errors, message, toast } = yield call(updateCompanySettingsRequest, id, dataForSave);

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			yield put(setCompanySettingsAction(data));
			redirect();
		} else {
			if (errors) {
				yield put(touch(FORM_SETTINGS_NAME, ...FIELDS_FOR_SETTINGS_VALIDATE));
				yield put(stopSubmit(FORM_SETTINGS_NAME, transformToValidationSettingErrors(errors)));
			}

			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsCompanySettingsSavingAction(false));
	}
}
export function* getCompanyAdminSettingsSaga({ payload }) {
	try {
		yield put(setIsCompanyAdminSettingsLoadingAction(true));

		const { data, message, toast } = yield call(getCompanyAdminSettingsRequest, payload);

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			yield put(setCompanyAdminSettingsAction(data));
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsCompanyAdminSettingsLoadingAction(false));
	}
}

export function* updateCompanyAdminSettingsSaga({ payload: { id, redirect } }) {
	try {
		yield put(setIsCompanyAdminSettingsSavingAction(true));

		const dataForSave = yield select(getDataForUpdateCompanyAdminSettingsSelector());

		const { data, errors, message, toast } = yield call(updateCompanyAdminSettingsRequest, id, dataForSave);

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			yield put(setCompanyAdminSettingsAction(data));
			redirect();
		} else {
			if (errors) {
				yield put(touch(FORM_SETTINGS_ADMIN_NAME, ...FIELDS_FOR_ADMIN_SETTINGS_VALIDATE));
				yield put(stopSubmit(FORM_SETTINGS_ADMIN_NAME, transformToValidationErrors(errors)));
			}

			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsCompanyAdminSettingsSavingAction(false));
	}
}
export function* getStagesSaga() {
	try {
		yield put(setIsStagesLoadingAction(true));
		const response = yield call(getStagesMyTransportsRequest);

		const { data, message, toast } = response;

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}
			yield put(setStagesAction(data));
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsStagesLoadingAction(false));
	}
}

export function* getDocumentsTypesSaga(action) {
	const { group } = action.payload;
	try {
		yield put(setIsDocumentsTypesLoadingAction(true));
		const response = yield call(getDocumentTypesRequest, group);

		const { data, message, toast } = response;

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}
			yield put(setDocumentsTypesAction(data));
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsDocumentsTypesLoadingAction(false));
	}
}

export default function* trailersContainerSaga() {
	yield takeEvery(GET_COMPANY_INFO, getCompanyInfoSaga);
	yield takeEvery(UPDATE_COMPANY, updateCompanySaga);
	yield takeEvery(GET_COMPANIES_TYPES, getCompaniesTypesSaga);
	yield takeEvery(GET_ORG_TYPES, getOrgTypesSaga);
	yield takeEvery(GET_TAX_SYSTEMS, getTaxSystemsSaga);
	yield debounce(300, GET_TAX_RATES, getTaxRatesSaga);
	yield debounce(300, GET_COMPANIES, getCompaniesSaga);
	yield takeEvery(GET_COMPANY_SETTINGS, getCompanySettingsSaga);
	yield takeEvery(UPDATE_COMPANY_SETTINGS, updateCompanySettingsSaga);
	yield takeEvery(GET_COMPANY_ADMIN_SETTINGS, getCompanyAdminSettingsSaga);
	yield takeEvery(UPDATE_COMPANY_ADMIN_SETTINGS, updateCompanyAdminSettingsSaga);
	yield takeEvery(GET_COMPANY_ABOUT_INFO, getCompaniesAboutSaga);
	yield takeEvery(CREATE_COMPANY_ABOUT_INFO, createCompaniesInfoSaga);
	yield takeEvery(GET_OPTIONS_TYPES, getModesTypesSaga);
	yield takeEvery(GET_OPTIONS_TYPES, getTonnagesTypesSaga);
	yield takeEvery(GET_OPTIONS_TYPES, getCarTypesSaga);
	yield takeEvery(GET_OPTIONS_TYPES, getDeliveryMethodsSaga);
	yield takeEvery(GET_STAGES, getStagesSaga);
	yield takeEvery(GET_DOCUMENTS_TYPES, getDocumentsTypesSaga);
}
